import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { Link } from "react-router-dom";
import { useInView } from "@/hooks/useInView";
import { useEffect, useRef, useState } from "react";
import SeoHead from "@/seo/SeoHead";
import { buildAutoRepairJsonLd, buildBreadcrumbJsonLd } from "@/seo/jsonLd";
import { useSiteContent } from "@/cms/SiteContentContext";
import { editableAttrs } from "@/cms/previewUtils";

interface CounterProps {
  target: number;
  suffix: string;
  active: boolean;
}

const Counter = ({ target, suffix, active }: CounterProps) => {
  const [count, setCount] = useState(0);
  const started = useRef(false);
  useEffect(() => {
    if (!active || started.current) return;
    started.current = true;
    const steps = 60;
    const increment = target / steps;
    let cur = 0;
    const t = setInterval(() => {
      cur += increment;
      if (cur >= target) {
        setCount(target);
        clearInterval(t);
      } else setCount(Math.floor(cur));
    }, 2000 / steps);
    return () => clearInterval(t);
  }, [active, target]);
  return (
    <span>
      {count.toLocaleString("tr-TR")}
      {suffix}
    </span>
  );
};

export default function HakkimizdaPage() {
  const { ref: statsRef, inView: statsInView } = useInView({ threshold: 0.3 });
  const { ref: timeRef, inView: timeInView } = useInView();
  const { content } = useSiteContent();
  const page = content.aboutPage;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      name: "Car Garage VAG Hakkımızda",
      description: page.hero.description,
    },
    buildBreadcrumbJsonLd([
      { name: "Ana Sayfa", path: "/" },
      { name: "Hakkımızda", path: "/hakkimizda" },
    ]),
    buildAutoRepairJsonLd(),
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#09090b" }}>
      <SeoHead title="Hakkımızda" description={page.hero.description} path="/hakkimizda" image="/images/hero-hakkimizda.jpg" jsonLd={jsonLd} />
      <Navbar />

      <section className="relative pt-36 pb-24 overflow-hidden border-b border-zinc-800/70" {...editableAttrs("aboutPage.hero", "object")}>
        <div className="absolute inset-0 z-0 bg-gradient-to-r from-zinc-950 via-[#130b0b] to-zinc-950"></div>
        <div className="absolute inset-0 z-0 opacity-[0.06]" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #dc2626 0%, transparent 35%), radial-gradient(circle at 80% 80%, #dc2626 0%, transparent 30%)" }}></div>
        <div className="absolute inset-0 z-0 bg-gradient-to-t from-zinc-950/80 via-transparent to-zinc-950/30"></div>
        <div className="absolute left-0 top-0 h-full w-1 bg-red-600 z-10"></div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-red-600/70 to-transparent z-10"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 mb-4 text-sm">
            <Link to="/" className="text-zinc-500 hover:text-red-500 transition-colors cursor-pointer">
              Ana Sayfa
            </Link>
            <span className="text-zinc-700">/</span>
            <span className="text-red-500">Hakkımızda</span>
          </div>
          <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-bold text-white mt-3 leading-tight" style={{ fontFamily: "Rajdhani, sans-serif" }}>
            {page.hero.title}
          </h1>
          <p className="text-zinc-300 text-lg mt-5 max-w-2xl leading-relaxed">{page.hero.description}</p>
        </div>
      </section>

      <section className="py-20 bg-zinc-950" {...editableAttrs("aboutPage.story", "object")}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.story.badge}
              </span>
              <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 mb-6 section-title-line" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                {page.story.titleLines[0]}
                <br />
                <span className="text-red-500">{page.story.titleLines[1]}</span>
                <br />
                {page.story.titleLines[2]}
              </h2>
              {page.story.paragraphs.map((paragraph) => (
                <p key={paragraph} className="text-zinc-300 text-base leading-relaxed mb-5">
                  {paragraph}
                </p>
              ))}
              <div className="grid grid-cols-2 gap-4" {...editableAttrs("aboutPage.story.features", "array")}>
                {page.story.features.map((f, index) => (
                  <div key={`${f}-${index}`} className="flex items-center gap-3">
                    <i className="ri-check-double-line text-red-500 text-base flex-shrink-0"></i>
                    <span className="text-zinc-300 text-sm">{f}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src={page.story.imageSrc}
                alt={page.story.imageAlt}
                className="w-full h-[500px] object-cover object-top"
                {...editableAttrs("aboutPage.story.imageSrc", "image")}
              />
              <div className="absolute -bottom-3 -right-3 w-24 h-24 border-b-4 border-r-4 border-red-600"></div>
              <div className="absolute -top-3 -left-3 w-24 h-24 border-t-4 border-l-4 border-red-600"></div>
              <div className="absolute bottom-8 left-8 bg-red-600/95 border border-red-300/40 shadow-[0_0_24px_rgba(220,38,38,0.35)] px-6 py-3.5">
                <div className="text-white text-base md:text-lg font-bold tracking-wide" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  {page.story.imageBadge}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-900" {...editableAttrs("aboutPage.stats", "array")}>
        <div ref={statsRef} className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {page.stats.map((stat) => (
              <div key={stat.label} className="text-center bg-zinc-800/40 py-10 px-6 border border-zinc-700/40 hover:border-red-600/40 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center mx-auto mb-3">
                  <i className={`${stat.icon} text-red-500 text-2xl`}></i>
                </div>
                <div className="text-4xl font-bold text-white mb-1" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                  <Counter target={stat.value} suffix={stat.suffix} active={statsInView} />
                </div>
                <div className="text-zinc-400 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-zinc-950" {...editableAttrs("aboutPage.timeline", "object")}>
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {page.timeline.badge}
            </span>
            <h2 className="text-4xl md:text-5xl font-bold text-white mt-3 section-title-line-center" style={{ fontFamily: "Rajdhani, sans-serif" }}>
              {page.timeline.title}
            </h2>
          </div>
          <div ref={timeRef} className="relative">
            <div className="absolute left-1/2 top-0 bottom-0 w-0.5 bg-zinc-800 hidden md:block"></div>
            <div className="flex flex-col gap-10">
              {page.timeline.items.map((m, idx) => (
                <div
                  key={m.year}
                  className={`flex flex-col md:flex-row items-start md:items-center gap-6 ${idx % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                  style={{
                    opacity: timeInView ? 1 : 0,
                    transform: timeInView ? "translateY(0)" : "translateY(20px)",
                    transition: `opacity 0.5s ease ${idx * 0.1}s, transform 0.5s ease ${idx * 0.1}s`,
                  }}
                >
                  <div className={`flex-1 ${idx % 2 === 0 ? "md:text-right" : "md:text-left"}`}>
                    <div className="bg-zinc-900 border border-zinc-800 hover:border-red-600/30 transition-colors p-5">
                      <h3 className="text-white font-bold text-lg" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                        {m.title}
                      </h3>
                      <p className="text-zinc-400 text-sm mt-1">{m.desc}</p>
                    </div>
                  </div>
                  <div className="relative z-10 hidden md:flex w-14 h-14 flex-shrink-0 items-center justify-center bg-red-600 text-white font-bold text-sm" style={{ fontFamily: "Rajdhani, sans-serif" }}>
                    {m.year}
                  </div>
                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
